import {
  Alert,
  Box,
  Button,
  CardActionArea,
  CardContent,
  Paper,
  TextareaAutosize,
  Typography,
} from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useState } from 'react';
import OutlinedFormControl from '../../../shared-components/OutlinedFormControl';
import { UserImportService } from './Services/UserImportService';

const useStyles = makeStyles({
  autoTextSize: {
    paddingTop: '8px',
    minWidth: '100%',
    maxWidth: '100%',
    outlineStyle: 'none',
    outlineColor: 'transparent',
    border: 'none',
    '&:focus, &:hover': {
      border: 'none',
    },
  },
  content: {
    backgroundColor: 'white',
    padding: '30px',
    boxShadow: '2px 2px 0 rgba(0,0,0,.1)',
    borderLeft: '1px solid #e0e0e0',
    borderTop: '1px solid #e0e0e0',
  },
});

const UserImport = () => {
  const classes = useStyles();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [idList, setIdList] = useState<string>('');
  const [isValid, setIsValid] = useState(true);
  const [isError, setIsError] = useState(false);

  const handleSubmit = () => {
    if (idList.length > 0) {
      setIsSubmitting(true);
      UserImportService.import(
        idList
          .replace(new RegExp(/^\s*[\r\n]/gm), '')
          .trimEnd()
          .split('\n'),
      )
        .then(s => {
          setIsComplete(true);
          setIsSubmitting(false);
          setIsError(!s);
        })
        .finally(() => {
          setIdList('');
        });
    } else {
      setIsValid(false);
    }
  };

  useEffect(() => {
    if ((idList.trimEnd().match(/\n/g) ?? '').length > 500) {
      setIsValid(false);
    } else {
      setIsValid(true);
    }
  }, [idList]);

  return (
    <>
      <Box display="flex">
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h1" gutterBottom>
            User Import
          </Typography>
        </Box>
      </Box>
      <Paper className={classes.content}>
        <div>
          <Typography variant="h5" gutterBottom>
            Enter a list of External Ids
          </Typography>
          <OutlinedFormControl
            label="User Ids"
            style={{ width: '100%' }}
            required
            error={!isValid}
          >
            <TextareaAutosize
              className={classes.autoTextSize}
              id="user-import-ids"
              aria-label="UserIDs"
              minRows={25}
              maxRows={25}
              placeholder="One ID per line"
              disabled={isSubmitting}
              onChange={e => setIdList(e.target.value)}
              onFocus={() => {
                if (!isSubmitting && isComplete) {
                  setIsComplete(false);
                }
              }}
              value={idList}
            />
          </OutlinedFormControl>
          {!isValid && idList.length > 0 && (
            <Typography variant="caption" color="error">
              Maximum 500 external IDs exceeded
            </Typography>
          )}
          {!isValid && idList.length === 0 && (
            <Typography variant="caption" color="error">
              Please enter one or more external ID(s)
            </Typography>
          )}
          <Typography variant="subtitle1">Maximum 500 user IDs</Typography>
        </div>
        <div
          style={{
            textAlign: 'right',
            paddingBottom: '16px',
          }}
        >
          <Button
            color="primary"
            variant="contained"
            disabled={isSubmitting}
            onClick={handleSubmit}
          >
            Start Import
          </Button>
        </div>
        {isSubmitting && (
          <div id="import-in-progress">
            <Typography variant="caption" sx={{ fontSize: '0.85em' }}>
              Import progress...
            </Typography>
            <LinearProgress />
          </div>
        )}
        {!isSubmitting && isComplete && (
          <div id="import-complete">
            <Alert severity="success">Import completed!</Alert>
          </div>
        )}
        {isError && (
          <div id="import-error">
            <Alert severity="warning">
              Import error. Please try importing again.
            </Alert>
          </div>
        )}
      </Paper>
    </>
  );
};

export default UserImport;
